import { useRef } from "react";

import { type DealPermissionUser } from "@vega-api/generated/types/dealPermissionUser";
import { DealPermissionUserStatus } from "@vega-api/generated/types/dealPermissionUserStatus";
import {
  addSelectItemFieldsToUser,
  type SearchMultiSelectItemWithUser,
} from "@vega-core/components/Form/Controls/Input/SearchUser/SearchUserMultiselect";

import { useDealRoles } from "../../hooks/useDealRoles";
import { useUserSearch } from "../InviteUsersModal/hooks/useUserSearch";

const EMPTY_SELECTED_USER_IDS: ReadonlySet<string> = new Set();

type UseRecipientAccessParams = {
  accessUsers: DealPermissionUser[];
  dealId: string;
  limitedPartnerId: string;
  seedUserIds: string[];
};

export const useRecipientAccess = ({
  accessUsers,
  dealId,
  limitedPartnerId,
  seedUserIds,
}: UseRecipientAccessParams) => {
  const { roleOptions, defaultRoleId, getRoleById, getRoleByName } =
    useDealRoles();

  const accessItems = accessUsers.map((user) =>
    addSelectItemFieldsToUser({
      id: user.id,
      fullName: user.fullName,
      email: user.email,
    }),
  );

  const activeAccessRoleById = new Map(
    accessUsers
      .filter((user) => user.status === DealPermissionUserStatus.Active)
      .map((user) => [user.id, user.role] as const),
  );

  const {
    users: searchResults,
    setSearchValue: setRecipientSearch,
    isPending: isRecipientSearchPending,
  } = useUserSearch({
    dealId,
    limitedPartnerId,
    selectedUserIds: EMPTY_SELECTED_USER_IDS,
  });

  const searchItems = searchResults.map((result) =>
    addSelectItemFieldsToUser({
      id: result.user.id,
      fullName: `${result.user.firstName} ${result.user.lastName}`.trim(),
      email: result.user.email,
    }),
  );

  const seenUserIds = useRef<Set<string>>(new Set());
  accessUsers.forEach((user) => seenUserIds.current.add(user.id));
  seedUserIds.forEach((id) => seenUserIds.current.add(id));
  searchItems.forEach((item) => seenUserIds.current.add(item.id));
  const knownUserIds: ReadonlySet<string> = seenUserIds.current;

  const poolById = new Map<string, SearchMultiSelectItemWithUser>(
    [...accessItems, ...searchItems].map((item) => [item.id, item] as const),
  );
  const poolItems = [...poolById.values()];

  return {
    poolItems,
    knownUserIds,
    activeAccessRoleById,
    defaultRoleId,
    roleOptions,
    getRoleById,
    getRoleByName,
    isRecipientSearchPending,
    setRecipientSearch,
  };
};
